/** The booking's cache actions (plan §3 "Cache") — one owner for the Actions menu's Cache
 *  group and the Cache tab's buttons, so both run the same thing and the tab shows the
 *  last result whichever started it:
 *
 *  - **Refresh mobile app cache…** (`console_bookings_vcache_refresh`): a confirmation
 *    that shows the publish verdict FIRST — a refresh of a booking the app must not show
 *    tombstones its entry — then the rebuild; the tab's records re-seed from its answer.
 *  - **Rebuild guide check-in list** (`console_bookings_checkin`) and **Rebuild
 *    tag-context cache** (`console_bookings_edit`): synchronous, internal, run at once
 *    (a snackbar says what happened). Disabled, saying why, for a booking with no start
 *    time / no guest — what the server would refuse.
 *  - **Resync chat membership…** (`console_bookings_edit`): remote (Twilio, call by call),
 *    so it confirms; the sync is queued, or already pending.
 *
 *  Absent without the permission. `host` renders the confirmations, anchored on the
 *  control that asked. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, type ReactNode } from 'react';

import {
  bookingActionKeys,
  rebuildCheckinManifest,
  rebuildTagContext,
  refreshBookingVcache,
  resyncChat,
  useBookingVcache,
  type BookingChatResync,
  type BookingCheckinManifestRebuild,
  type BookingTagContextRebuild,
  type BookingVcache,
  type BookingVcacheRefreshResult,
} from '@/api/bookingActions';
import type { BookingRecordEditContext } from '@/api/bookingRecord';
import { useSnackbar } from '@/components/Snackbar';
import type { RecordActionItem } from '@/features/records/page/RecordActionsMenu';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelative, formatZoned } from '@/utils/zonedTime';

import { ImmediateActionPopover, refusalText } from './ImmediateActionPopover';
import { PublishVerdict, RefreshEffect } from './VcacheVerdict';

export const CACHE_PERM = {
  vcacheRefresh: 'console_bookings_vcache_refresh',
  checkin: 'console_bookings_checkin',
  edit: 'console_bookings_edit',
} as const;

export interface Ran<R> {
  value: R;
  /** When it ran (ISO). */
  at: string;
}

/** One cache action as the Cache tab offers it. Absent → the operator may not run it. */
export interface CacheActionControl<R> {
  disabledReason: string | null;
  pending: boolean;
  last: Ran<R> | null;
  /** `anchorEl`: where a confirmation hangs (ignored by the run-at-once actions). */
  start: (anchorEl: HTMLElement | null) => void;
}

export interface BookingCacheActions {
  vcacheRefresh: CacheActionControl<BookingVcacheRefreshResult> | null;
  manifest: CacheActionControl<BookingCheckinManifestRebuild> | null;
  tagContext: CacheActionControl<BookingTagContextRebuild> | null;
  chat: CacheActionControl<BookingChatResync> | null;
}

const inWords = (s: number | null) => (s == null ? null : formatRelative(new Date(Date.now() + s * 1000)));

export function manifestText(r: BookingCheckinManifestRebuild): string {
  if (r.present) {
    const when = inWords(r.expires_in_s);
    return `Check-in list rebuilt — stored until the window closes${when ? ` (${when})` : ''}.`;
  }
  return r.window_open
    ? 'Check-in list rebuilt, but nothing is stored for this departure.'
    : 'No check-in list now: the check-in window is not open, so the list was removed.';
}

export function tagContextText(r: BookingTagContextRebuild): string {
  if (!r.cached) return 'Tag-context cache removed: the trip is long over.';
  const when = inWords(r.expires_in_s);
  return `Tag-context cache rebuilt${r.keys.length ? ` (${r.keys.join(', ')})` : ''}${when ? ` — expires ${when}` : ''}.`;
}

export function chatText(r: BookingChatResync): string {
  if (!r.queued) return 'A full chat sync of this departure was already pending — it covers this booking.';
  // A calendar date (the tour's local travel date), not an instant: read it as is.
  const day = r.travel_date ? ` on ${formatZoned(`${r.travel_date}T12:00:00Z`, 'UTC', { time: false, zone: false })}` : '';
  return `Chat resync queued for tour #${r.group_tour_id}${day}.`;
}

export function vcacheText(r: BookingVcacheRefreshResult): string {
  const lost = r.uncached_versions > 0 ? ` ${r.uncached_versions} spec version(s) could not be cached.` : '';
  switch (r.effect) {
    case 'unpublish':
      return `Tombstoned: the app removes the booking.${lost}`;
    case 'none':
      return `Nothing changed: the trip is long over.${lost}`;
    default:
      return `Refreshed: tour_participant rewritten.${lost}`;
  }
}

function VcacheRefreshPopover({
  bookingId,
  anchorEl,
  onClose,
  run,
}: {
  bookingId: number;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  run: () => Promise<BookingVcacheRefreshResult>;
}) {
  // The verdict as it is now — the same query the Cache tab reads.
  const q = useBookingVcache(bookingId);
  const publish = q.data?.publish;
  return (
    <ImmediateActionPopover
      anchorEl={anchorEl}
      onClose={onClose}
      title="Refresh the mobile app cache"
      confirmLabel={publish?.refresh_effect === 'unpublish' ? 'Refresh — remove it from the app' : 'Refresh now'}
      confirmDisabled={!publish || publish.refresh_effect === 'none'}
      run={run}
      renderResult={(r) => (
        <>
          <PublishVerdict publish={r.publish} />
          <span>{vcacheText(r)}</span>
        </>
      )}
      dataKey="vcache_refresh"
    >
      {publish ? (
        <>
          <PublishVerdict publish={publish} />
          <RefreshEffect publish={publish} />
        </>
      ) : q.error ? (
        <span>The verdict could not be loaded — {refusalText(q.error)}</span>
      ) : (
        <span role="status">Checking whether the app shows it…</span>
      )}
    </ImmediateActionPopover>
  );
}

/** The confirmation of "Resync chat membership" (remote, Twilio call by call), on the
 *  control that asked — the booking's Cache actions and a trip row's menu on the user
 *  record run the same one. */
export function ChatResyncPopover({
  anchorEl,
  onClose,
  run,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  run: () => Promise<BookingChatResync>;
}) {
  return (
    <ImmediateActionPopover
      anchorEl={anchorEl}
      onClose={onClose}
      title="Resync chat membership"
      confirmLabel="Queue the resync"
      run={run}
      renderResult={(r) => <span>{chatText(r)}</span>}
      dataKey="chat_resync"
    >
      <span>
        Queues one full chat sync of this tour’s channel and threads on the travel date. It runs in the background and
        talks to Twilio call by call; a sync already pending covers this booking.
      </span>
    </ImmediateActionPopover>
  );
}

export function useBookingCacheActions({
  bookingId,
  editContext,
}: {
  bookingId: number;
  editContext: BookingRecordEditContext;
}): { cache: BookingCacheActions; host: ReactNode } {
  const { hasPerm } = usePermissions();
  const qc = useQueryClient();
  const snackbar = useSnackbar();

  const [vcacheAnchor, setVcacheAnchor] = useState<HTMLElement | null>(null);
  const [chatAnchor, setChatAnchor] = useState<HTMLElement | null>(null);
  const [last, setLast] = useState<{
    vcache: Ran<BookingVcacheRefreshResult> | null;
    manifest: Ran<BookingCheckinManifestRebuild> | null;
    tagContext: Ran<BookingTagContextRebuild> | null;
    chat: Ran<BookingChatResync> | null;
  }>({ vcache: null, manifest: null, tagContext: null, chat: null });
  const stamp = useCallback(<K extends keyof typeof last>(k: K, value: NonNullable<(typeof last)[K]>['value']) => {
    setLast((prev) => ({ ...prev, [k]: { value, at: new Date().toISOString() } }));
  }, []);

  const manifest = useMutation({
    mutationFn: () => rebuildCheckinManifest(bookingId),
    onSuccess: (r) => {
      stamp('manifest', r);
      snackbar.show(manifestText(r), 'success');
    },
    onError: (e) => snackbar.show(`Check-in list not rebuilt: ${refusalText(e)}`, 'error'),
  });
  const tagContext = useMutation({
    mutationFn: () => rebuildTagContext(bookingId),
    onSuccess: (r) => {
      stamp('tagContext', r);
      snackbar.show(tagContextText(r), 'success');
    },
    onError: (e) => snackbar.show(`Tag-context cache not rebuilt: ${refusalText(e)}`, 'error'),
  });

  const runVcache = async () => {
    const r = await refreshBookingVcache(bookingId);
    stamp('vcache', r);
    qc.setQueryData<BookingVcache>(bookingActionKeys.vcache(bookingId), (old) => ({
      booking_id: r.booking_id,
      generated_ts: r.refreshed_ts,
      spec_versions: old?.spec_versions ?? [],
      publish: r.publish,
      entries: r.entries,
    }));
    return r;
  };
  const runChat = async () => {
    const r = await resyncChat(bookingId);
    stamp('chat', r);
    return r;
  };

  const noStart = editContext.schedule.start_ts ? null : 'No start time, so no check-in list';
  const noGuest = editContext.guest ? null : 'The booking has no guest';

  const cache: BookingCacheActions = {
    vcacheRefresh: hasPerm(CACHE_PERM.vcacheRefresh)
      ? { disabledReason: null, pending: false, last: last.vcache, start: (a) => setVcacheAnchor(a) }
      : null,
    manifest: hasPerm(CACHE_PERM.checkin)
      ? { disabledReason: noStart, pending: manifest.isPending, last: last.manifest, start: () => manifest.mutate() }
      : null,
    tagContext: hasPerm(CACHE_PERM.edit)
      ? { disabledReason: noGuest, pending: tagContext.isPending, last: last.tagContext, start: () => tagContext.mutate() }
      : null,
    chat: hasPerm(CACHE_PERM.edit)
      ? { disabledReason: null, pending: false, last: last.chat, start: (a) => setChatAnchor(a) }
      : null,
  };

  const host = (
    <>
      {vcacheAnchor && (
        <VcacheRefreshPopover
          bookingId={bookingId}
          anchorEl={vcacheAnchor}
          onClose={() => setVcacheAnchor(null)}
          run={runVcache}
        />
      )}
      {chatAnchor && <ChatResyncPopover anchorEl={chatAnchor} onClose={() => setChatAnchor(null)} run={runChat} />}
    </>
  );

  return { cache, host };
}

/** The Actions menu's Cache group: the tab, then each action the operator may run. */
export function cacheMenuItems(cache: BookingCacheActions, onInspect: () => void): RecordActionItem[] {
  const items: RecordActionItem[] = [{ key: 'vcache', label: 'Inspect mobile app cache', onSelect: onInspect }];
  const add = (key: string, label: string, c: CacheActionControl<unknown> | null, hint?: string) => {
    if (!c) return;
    items.push({
      key,
      label,
      ...(c.disabledReason ? { disabled: true, description: c.disabledReason } : hint ? { hint } : {}),
      onSelect: (anchorEl) => c.start(anchorEl),
    });
  };
  add('vcache_refresh', 'Refresh mobile app cache…', cache.vcacheRefresh);
  add('checkin_manifest', 'Rebuild guide check-in list', cache.manifest, 'runs now');
  add('tag_context', 'Rebuild tag-context cache', cache.tagContext, 'runs now');
  add('chat_resync', 'Resync chat membership…', cache.chat, 'Twilio');
  return items;
}
